// PollResults.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useUser } from 'context/UserContext';

// Services
import { listPollVotes } from 'services/polls/PollReadService';

// CSS
import './PollResults.css';

/**
 * PollResults
 *
 * Displays final or ongoing poll results for a poll node.
 *
 * @returns {ReactNode} The rendered component.
 */
const PollResults = ({ node }) => {

    // ----- CONTEXT ----- //

    const { user } = useUser();

    // Extract options from pollinfo
    const options = node?.pollinfo?.options || [];

    // ----- STATE ----- //

    const [voteCounts, setVoteCounts] = useState([]); // array of count per option index
    const [totalVotes, setTotalVotes] = useState(0);
    const [selectedIndex, setSelectedIndex] = useState(null);

    // ----- MEMOS ----- //

    // Compute time remaining

    const durationRemaining = useMemo(() => {
        const endsAt = node?.pollinfo?.endsAt;
        if (!endsAt || typeof endsAt.toMillis !== 'function') return '';
        const msRemaining = endsAt.toMillis() - Date.now();
        if (msRemaining <= 0) return 'Final results';

        const minutes = Math.floor(msRemaining / (60 * 1000)) % 60;
        const hours = Math.floor(msRemaining / (60 * 60 * 1000)) % 24;
        const days = Math.floor(msRemaining / (24 * 60 * 60 * 1000));

        const parts = [];
        if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
        if (hours > 0) parts.push(`${hours} hr${hours > 1 ? 's' : ''}`);
        if (minutes > 0 && days === 0) parts.push(`${minutes} min`);
        return parts.length > 0 ? `${parts.join(' ')} left` : 'Less than a minute left';
    }, [node?.pollinfo?.endsAt]);

    // ----- EFFECTS ----- //

    // Load all votes for this poll node

    useEffect(() => {
        const loadVotes = async () => {
            try {
                const votes = await listPollVotes(node.key);

                const counts = new Array(options.length).fill(0);
                let userVoteIndex = null;

                votes.forEach((vote) => {
                    const i = vote.optionIndex;

                    if (typeof i === 'number' && i >= 0 && i < options.length) {
                        counts[i]++;
                    }

                    if (vote.id === user.key) {
                        userVoteIndex = i;
                    }
                });

                setVoteCounts(counts);
                setTotalVotes(votes.length);
                setSelectedIndex(userVoteIndex);
            } catch (err) {
                console.error('Failed to load poll results:', err);
            }
        };

        if (node?.key && user?.key) {
            loadVotes();
        }
    }, [node?.key, user?.key, options.length]);

    // ----- UTILITIES ----- //

    // Compute percentage safely

    const getPercentage = (count) => {
        return totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
    };

    // ----- RENDER ----- //

    return (
        <div className="poll-results-container">

            {/* TITLE */}
            <div className="poll-results-title">{node?.title}</div>

            {/* OPTIONS */}
            <div className="poll-results-options">
                {options.map((option, index) => {
                    const percent = getPercentage(voteCounts[index] || 0);
                    const isSelected = selectedIndex === index;

                    return (

                        <div
                          className={`poll-results-option ${isSelected ? 'selected' : ''}`}
                          key={index}
                        >

                            {/* BAR */}
                            <div
                              className="poll-results-bar"
                              style={{
                                width: `${percent}%`,
                              }}
                            />

                            {/* TEXT */}
                            <div className="poll-results-text">

                                {/* LABEL */}
                                <span className="poll-results-label">{option}</span>

                                {/* PERCENT */}
                                <span className="poll-results-percent">{percent}%</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* FOOTER */}
            <div className="poll-results-footer">
                <span className="poll-results-count">{totalVotes} vote{totalVotes !== 1 ? 's' : ''}</span>

                {/* DIVIDER */}
                <span className="poll-results-divider">•</span>
                <span className="poll-results-time">{durationRemaining}</span>
            </div>
        </div>
    );
};

// Dev Tools
PollResults.displayName = 'PollResults';

export default PollResults;
